import {
  Box,
  Divider,
  Flex,
  Icon,
  IconButton,
  Portal,
  Skeleton,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { FC, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BiLockAlt } from 'react-icons/bi'
import { BsPinAngle, BsPinAngleFill } from 'react-icons/bs'
import { IoMdCheckmark } from 'react-icons/io'
import { IoCloseOutline, IoSettingsOutline } from 'react-icons/io5'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../ui-kit'
import { PermissionsEnum } from '../../../utils'
import { isRestrictedUser, isViewOnlyUser } from '../../../utils/roles'
import useToast from '../../hooks/useToast'
import {
  ListSavedSearchSuccessResponse,
  SavedSearchErrorResponse,
} from '../../interfaces'
import { NavBarContext, UserContext } from '../../providers'
import PermissionGuard from '../PermissionGuard'
import ManageSavedSearchModal from '../SavedSearch/ManageSavedSearchModal'
import SearchBar from '../SearchBar'

interface SavedSearchesDrawerProps {
  isNavbarOpen?: boolean
  isOpenSavedSearches: boolean
  onCloseSavedSearches: () => void
}

interface CheckboxProps {
  label: string
  isChecked: boolean
  setIsChecked: (isChecked: boolean) => void
}

const ExtendedMenuHeading = styled.span`
  text-transform: uppercase;
  font-size: 12px;
  height: 18px;
  color: ${colors.appSlate};
`

const ExtendedMenuItem: FC<
  ListSavedSearchSuccessResponse & {
    getAndSetSavedSearches: () => void
    onCloseSavedSearches: () => void
  }
> = ({
  searchId,
  name,
  url,
  isPrivate,
  isPinned,
  createdBy,
  updatedAt,
  createdAt,
  getAndSetSavedSearches,
  onCloseSavedSearches,
}) => {
    const { t } = useTranslation()
    const [isHovered, setIsHovered] = useState<boolean>(false)
    const {
      isOpen: isOpenManageSavedSearchModal,
      onOpen: onOpenManageSavedSearchModal,
      onClose: onCloseManageSavedSearchModal,
    } = useDisclosure()
    const { request } = useFetch({
      cachePolicy: CachePolicies.NO_CACHE,
    })
    const { user } = useContext(UserContext)
    const toast = useToast()
    const history = useHistory()

    const updateIsPinned = async (isPinned: boolean) => {
      const res = (await request.put(
        `/api/media-library/${user?.clientNumber}/saved-searches/${searchId}/pin`,
        { isPinned },
      )) as ListSavedSearchSuccessResponse | SavedSearchErrorResponse

      if (request.error) {
        toast({
          description:
            (res as SavedSearchErrorResponse).message ??
            t(
              'error_in_pinning',
              'Error in pinning saved search, please try again later.',
            ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        getAndSetSavedSearches()
      }
    }

    const truncatedName =
      name && name.length > 250 ? name.slice(0, 250) + '...' : name

    return (
      <>
        <Flex
          key={searchId}
          color="appSlate"
          fontWeight="500"
          borderRadius="6px"
          width="full"
          p="8px 8px 8px 4px"
          align="center"
          cursor="pointer"
          _hover={{ bg: 'appPaleGreyThree' }}
          onClick={() => {
            const parsedUrl = new URL(url)
            let pathWithinDashboard = parsedUrl.pathname.replace(
              /^\/(?:dashboard|media-library-widget)/,
              '',
            )

            if (pathWithinDashboard === '') {
              pathWithinDashboard = '/'
            }

            const pathWithSearch = `${pathWithinDashboard}${parsedUrl.search}${parsedUrl.hash}`
            history.push(pathWithSearch)
            onCloseSavedSearches()
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Tooltip
            label={isPinned ? t('unpin', 'Unpin') : t('pin', 'Pin')}
            hasArrow
          >
            <IconButton
              aria-label={t('pin_search', 'Pin or unpin search')}
              icon={isPinned ? <BsPinAngleFill /> : <BsPinAngle />}
              variant="link"
              minWidth="16px"
              borderRadius="4px"
              height="16px"
              color="appSlate"
              mr="8px"
              onClick={async e => {
                e.stopPropagation()
                if (
                  user?.permissions.includes(
                    PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_VIEW,
                  )
                ) {
                  await updateIsPinned(!isPinned)
                }
              }}
            />
          </Tooltip>
          <Tooltip label={truncatedName} hasArrow>
            <Text
              className="ik-saved-search-name"
              color={isHovered ? 'appBlue' : 'appSlate'}
              isTruncated
              align="center"
              maxW="75%"
            >
              {name}
            </Text>
          </Tooltip>
          <Flex ml="auto">
            <PermissionGuard
              permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE}
            >
              {(hasPermission: boolean) => {
                const shouldShow =
                  isHovered &&
                  (!isRestrictedUser(user?.roles) ||
                    user?.id === createdBy.userId)
                return hasPermission && shouldShow ? (
                  <Tooltip
                    label={t('manage_saved_search', 'Manage saved search')}
                    hasArrow
                  >
                    <IconButton
                      aria-label={t('manage_saved_search', 'Manage saved search')}
                      icon={<IoSettingsOutline />}
                      variant="link"
                      minWidth="16px"
                      borderRadius="4px"
                      height="16px"
                      color="appSlate"
                      onClick={e => {
                        e.stopPropagation()
                        onOpenManageSavedSearchModal()
                      }}
                    />
                  </Tooltip>
                ) : (
                  <></>
                )
              }}
            </PermissionGuard>
            {isPrivate && (
              <Tooltip
                label={t('visible_only_to_me', 'Visible only to me')}
                hasArrow
              >
                <Box ml="4px" display="flex" alignItems="center">
                  <Icon
                    aria-label={t('visibility', 'visibility')}
                    as={BiLockAlt}
                    minWidth="16px"
                    borderRadius="4px"
                    height="16px"
                    color="appBlueyGrey"
                  />
                </Box>
              </Tooltip>
            )}
          </Flex>
        </Flex>
        {isOpenManageSavedSearchModal && (
          <ManageSavedSearchModal
            name={name}
            url={url}
            searchId={searchId}
            isPrivate={isPrivate}
            createdBy={createdBy}
            isOpen={isOpenManageSavedSearchModal}
            onClose={onCloseManageSavedSearchModal}
            getAndSetSavedSearches={getAndSetSavedSearches}
          />
        )}
      </>
    )
  }

const CheckBox: FC<CheckboxProps> = ({ label, isChecked, setIsChecked }) => {
  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      gridColumnGap={2}
      height="24px"
      bg={isChecked ? '#E9F4FF' : '#FFF'}
      border="1px solid"
      borderColor="#E6E7EA"
      borderRadius="16px"
      cursor="pointer"
      justifyContent="center"
      px="8px"
      py="3px"
      onClick={() => setIsChecked(!isChecked)}
    >
      <Text color={isChecked ? '#0450D5' : '#4A5568'} fontSize="12px">
        {label}
      </Text>
      {isChecked && <IoMdCheckmark color="#0450D5" size="14px" />}
    </Box>
  )
}

const SavedSearchesDrawer: FC<SavedSearchesDrawerProps> = ({
  isNavbarOpen,
  isOpenSavedSearches,
  onCloseSavedSearches,
}) => {
  const { t } = useTranslation()
  const toast = useToast()
  const [searchQuery, setSearchQuery] = useState<string>('')
  const { savedSearches, setSavedSearches } = useContext(NavBarContext)
  const [showOnlyToMe, setShowOnlyToMe] = useState<boolean>(true)
  const [showSharedWithEveryone, setShowSharedWithEveryone] =
    useState<boolean>(true)
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const { user } = useContext(UserContext)

  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true)

  const getAndSetSavedSearches = async () => {
    const res = (await request.get(
      `/api/media-library/${user?.clientNumber}/saved-searches`,
    )) as ListSavedSearchSuccessResponse[]

    if (request.error) {
      toast({
        description: t(
          'error_in_getting_saved_searches',
          'Error in getting saved searches, please try again later.',
        ),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } else if (Array.isArray(res)) {
      setSavedSearches(res)
    }
    setIsInitialLoading(false)
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseSavedSearches()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  useEffect(() => {
    getAndSetSavedSearches()
  }, [])

  const filteredSavedSearches = useMemo(() => {
    let filtered = savedSearches

    if (showOnlyToMe && !showSharedWithEveryone) {
      filtered = filtered.filter(item => item.isPrivate === true)
    } else if (!showOnlyToMe && showSharedWithEveryone) {
      filtered = filtered.filter(item => item.isPrivate === false)
    } else if (!showOnlyToMe && !showSharedWithEveryone) {
      filtered = []
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(query),
      )
    }
    return filtered
  }, [savedSearches, searchQuery, showOnlyToMe, showSharedWithEveryone])

  return (
    <Portal>
      <Flex
        direction="column"
        position="absolute"
        top="0"
        bottom="0"
        left={
          !window.isMediaLibraryWidget ? (isNavbarOpen ? '260px' : '72px') : '0'
        }
        width="312px"
        height="100vh"
        backgroundColor="#fbfcff"
        borderLeft="solid 1px #e2e8f0"
        borderRight="solid 1px #e2e8f0"
        boxShadow="-2px 0 18px 0 rgba(0, 0, 0, 0.16)"
        zIndex="98"
        px="16px"
        py="14px"
        transition="left var(--sidebarNavTransitionDuration) ease-in-out"
      >
        <Flex justify="space-between" align="center" mb="18px">
          <ExtendedMenuHeading>
            {t('saved_searches', 'Saved searches')}
          </ExtendedMenuHeading>
          <IconButton
            aria-label={t('close', 'Close')}
            icon={<IoCloseOutline />}
            color="appBlueyGrey"
            variant="link"
            minWidth="18px"
            borderRadius="4px"
            height="18px"
            onClick={onCloseSavedSearches}
            _hover={{ background: colors.appGrey }}
          />
        </Flex>

        <SearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          placeholder={t(
            'saved_search_search_placeholder',
            'Find a saved search',
          )}
          showSearchIcon
        />

        <Flex mt="8px" gap="4px">
          <CheckBox
            label={t('only_to_me', 'Only to me')}
            isChecked={showOnlyToMe}
            setIsChecked={setShowOnlyToMe}
          />
          <CheckBox
            label={t('shared_with_everyone', 'Shared with everyone')}
            isChecked={showSharedWithEveryone}
            setIsChecked={setShowSharedWithEveryone}
          />
        </Flex>

        <Divider my="12px" color="appPaleBlue" />

        <Box
          height="100%"
          overflowY="auto"
          css={{
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            '-ms-overflow-style': 'none',
            scrollbarWidth: 'none',
          }}
        >
          {isInitialLoading ? (
            <>
              <Skeleton
                height="34px"
                width="full"
                borderRadius="6px"
                mb="8px"
              />
              <Skeleton
                height="34px"
                width="full"
                borderRadius="6px"
                mb="8px"
              />
              <Skeleton height="34px" width="full" borderRadius="6px" />
            </>
          ) : savedSearches.length === 0 ? (
            <Flex
              height="100%"
              align="center"
              justify="center"
              color="appSlate"
            >
              <Text>{t('no_searches_saved', 'No searches saved')}</Text>
            </Flex>
          ) : filteredSavedSearches.length === 0 ? (
            <Flex
              height="100%"
              align="center"
              justify="center"
              color="appSlate"
            >
              <Text>{t('no_matches_found', 'No matches found')}</Text>
            </Flex>
          ) : (
            <AnimatePresence>
              {filteredSavedSearches.map(savedSearch => (
                <motion.div key={savedSearch.searchId} layout>
                  <ExtendedMenuItem
                    {...savedSearch}
                    getAndSetSavedSearches={getAndSetSavedSearches}
                    onCloseSavedSearches={onCloseSavedSearches}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </Box>
      </Flex>
    </Portal>
  )
}

export default SavedSearchesDrawer
